import {
  parseAgentCashCaptureBytes,
  parseRouteNoteBytes,
  parseRouteNoteOfferBytes,
  routeId,
  verifyRouteNotePublisherSignature,
  verifySignedRouteNoteOffer,
  type RouteNote,
  type RouteNoteOffer,
  type RouteNoteOfferVerification,
} from "@boon/x402-route";
import { isHex, type Hex } from "viem";

const encoder = new TextEncoder();

export function isSafeBrowserNavigationMethod(method: string): boolean {
  return method === "GET";
}

export interface RouteNoteBundleVerification {
  readonly note: RouteNote;
  readonly offer: RouteNoteOffer;
  readonly routeId: Hex;
  readonly signature: RouteNoteOfferVerification;
  readonly challengeProvided: boolean;
  readonly challengeBound: boolean;
  readonly noteExpired: boolean;
}

export async function verifyRouteNoteBundle(input: {
  readonly noteJson: string;
  readonly offerJson: string;
  readonly signature: string;
  readonly agentCashCaptureJson?: string;
  readonly now?: number;
}): Promise<RouteNoteBundleVerification> {
  if (!isHex(input.signature) || !/^0x(?:[0-9a-fA-F]{128}|[0-9a-fA-F]{130})$/.test(input.signature)) {
    throw new Error("Signature must be a 64-byte compact or 65-byte EVM signature.");
  }
  const noteBytes = encoder.encode(input.noteJson);
  const offerBytes = encoder.encode(input.offerJson);
  const note = parseRouteNoteBytes(noteBytes);
  const offer = parseRouteNoteOfferBytes(offerBytes);
  const digest = routeId(note.route);
  const captureText = input.agentCashCaptureJson?.trim();
  const signature = captureText
    ? await verifySignedRouteNoteOffer(offerBytes, input.signature as Hex, {
        capture: parseAgentCashCaptureBytes(encoder.encode(captureText)),
        noteBytes,
        now: input.now,
      })
    : await verifyRouteNotePublisherSignature(offerBytes, input.signature as Hex, {
        noteBytes,
        now: input.now,
      });
  return {
    note,
    offer,
    routeId: digest,
    signature,
    challengeProvided: Boolean(captureText),
    challengeBound: Boolean(captureText) && signature.valid,
    noteExpired: note.expiresAt <= (input.now ?? Math.floor(Date.now() / 1_000)),
  };
}
